import { useEffect, useState } from "react";

import GenericTable from "@/components/GenericTable";
import GenericTableElement from "@/components/GenericTableElement";
import { toaster } from "@/components/ui/toaster";
import { INVOICE_TABLE_COLUMNS } from "@/config/tables";
import { date, now } from "@/helpers/time";
import { useMerchant } from "@/hooks/useMerchant";
import { hasSeenTour, onTourSeen } from "@/hooks/useTour";
import { CreateInvoice, Invoice } from "@/types/types";
import { IconButton, Menu, Portal, Spinner, Table } from "@chakra-ui/react";
import {
  Archive,
  Ban,
  Banknote,
  BanknoteX,
  ClipboardList,
  CreditCard,
  DollarSign,
  Download,
  Hammer,
  MoreVertical,
  Pencil,
  PencilOff,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { useCancelInvoice } from "../hooks/useCancelInvoice";
import { useDownloadInvoice } from "../hooks/useDownloadInvoice";
import { useEditCreateInvoice } from "../hooks/useEditCreateInvoice";
import { useMarkManualErpSync } from "../hooks/useMarkManualErpSync";
import { useRetryErpSync } from "../hooks/useRetryErpSync";
import { useSetCardPaymentActivation } from "../hooks/useSetCardPaymentActivation";
import { useSetCancelledPaymentActivation } from "../hooks/useSetCancelledPaymentActivation";
import { useSetPartialPaymentActivation } from "../hooks/useSetPartialPaymentActivation";
import EditorsModal from "./EditorsModal";
import UnsuccessfulPaymentsModal from "./UnsuccessfulPaymentsModal";
import AddInvoiceModal from "./addInvoiceModal";
import PaymentInfoModal from "./paymentInfoModal";
import { usePermissions } from "@/hooks/usePermissions";

type Props = {
  isLoading: boolean;
  invoices: Invoice[];
  refetch: () => void;
};

// A first-time user lands on an empty table, so the section tour has nothing
// to point at; these render one row per estado until the tour is completed.
const DEMO_INVOICES = [
  {
    id: "ejemplo-1",
    propertyCode: "APTO-101",
    customerDocumentNumber: "1234567890",
    description: "Arriendo (dato de ejemplo)",
    dueDate: "2026-09-05",
    amount: 1_500_000,
    status: "ready",
    creationMethod: "manual",
    sent: false,
  },
  {
    id: "ejemplo-2",
    propertyCode: "CASA-202",
    customerDocumentNumber: "9876543210",
    description: "Arriendo (dato de ejemplo)",
    dueDate: "2026-09-05",
    amount: 2_300_000,
    status: "paid",
    creationMethod: "manual",
    sent: true,
  },
  {
    id: "ejemplo-3",
    propertyCode: "LOCAL-303",
    customerDocumentNumber: "5556667770",
    description: "Arriendo (dato de ejemplo)",
    dueDate: "2026-09-05",
    amount: 3_100_000,
    status: "cancelled",
    creationMethod: "manual",
    sent: true,
  },
] as Invoice[];

const InvoicesTable = ({
  isLoading,
  invoices,
  refetch,
}: Props): JSX.Element => {
  const { merchant } = useMerchant();
  const { isEditor, isAdmin } = usePermissions();
  const [demoEligible, setDemoEligible] = useState(
    () => !hasSeenTour("recaudos")
  );
  useEffect(() => onTourSeen("recaudos", () => setDemoEligible(false)), []);
  const showDemo = demoEligible && !isLoading && invoices.length === 0;
  const rows = showDemo ? DEMO_INVOICES : invoices;

  const { setPartialPaymentActivation, isPending: partialPaymentPending } =
    useSetPartialPaymentActivation(
      () => {
        toaster.create({
          title: "Recaudo actualizado",
          description: "El pago parcial se actualizó exitosamente.",
          type: "success",
          duration: 5000,
          meta: { closable: true },
        });
      },
      () => {
        toaster.create({
          title: "Error",
          description: "Hubo un error al actualizar el pago parcial.",
          type: "error",
          duration: 5000,
          meta: { closable: true },
        });
      }
    );

  const { setCancelledPaymentActivation, isPending: overduePaymentPending } =
    useSetCancelledPaymentActivation(
      (active) => {
        toaster.create({
          title: active ? "Pago habilitado" : "Pago deshabilitado",
          description: active
            ? "La factura se habilitó para pago."
            : "La factura se deshabilitó para pago.",
          type: "success",
          duration: 5000,
          meta: { closable: true },
        });
      },
      () => {
        toaster.create({
          title: "Error",
          description: "Hubo un error al actualizar el pago vencido.",
          type: "error",
          duration: 5000,
          meta: { closable: true },
        });
      }
    );

  ///////////////////// CARD PAYMENT ACTIVATION ///////////////////////////
  const [cardActivationInvoiceIds, setCardActivationInvoiceIds] = useState<
    Set<string>
  >(new Set());

  const handleCardActivation = (
    merchantId: string,
    invoiceId: string,
    active: boolean
  ) => {
    setCardActivationInvoiceIds((prev) => new Set(prev).add(invoiceId));

    setCardPaymentActivation(merchantId, invoiceId, active, {
      onSettled: () => {
        setCardActivationInvoiceIds((prev) => {
          const updated = new Set(prev);
          updated.delete(invoiceId);
          return updated;
        });
      },
    });
  };

  const { setCardPaymentActivation } = useSetCardPaymentActivation(
    () => {
      toaster.create({
        title: "Tarjeta activada",
        description: "La tarjeta se ha activado exitosamente.",
        type: "success",
        duration: 5000,
        meta: { closable: true },
      });
    },
    () => {
      toaster.create({
        title: "Error",
        description: "Hubo un error al activar la tarjeta.",
        type: "error",
        duration: 5000,
        meta: { closable: true },
      });
    }
  );

  const cardDaysBeforeExpiration =
    merchant?.paymentMethods.card.daysBeforeExpiration;
  const cardExpirationCutoff = now().add(cardDaysBeforeExpiration ?? 0, "days");

  const cardDaysBeforeEndOfMonth =
    merchant?.paymentMethods.card.daysBeforeEndOfMonth;
  const cardEndOfMonthCutoff = now().add(cardDaysBeforeEndOfMonth ?? 0, "days");

  const cardDaysAfterDueDate = merchant?.paymentMethods.card.daysAfterDueDate;
  const cardDueDateCutoff = now().subtract(cardDaysAfterDueDate ?? 0, "days");

  const isInvoiceCardDisabled = (invoice: Invoice) =>
    !merchant?.paymentMethods.card.active &&
    !(cardExpirationCutoff > date(invoice.expirationDate).endOf("day")) &&
    !(cardEndOfMonthCutoff > now().endOf("month")) &&
    !(
      cardDaysAfterDueDate &&
      cardDueDateCutoff > date(invoice.dueDate).endOf("day")
    );

  ////////////////////////// DOWNLOADING //////////////////////////////////
  const [downloadingInvoiceIds, setDownloadingInvoiceIds] = useState<
    Set<string>
  >(new Set());

  const { downloadPdf } = useDownloadInvoice();

  const handleDownloadInvoice = (merchantId: string, invoiceId: string) => {
    setDownloadingInvoiceIds((prev) => new Set(prev).add(invoiceId));

    downloadPdf(merchantId, invoiceId).finally(() => {
      setDownloadingInvoiceIds((prev) => {
        const updated = new Set(prev);
        updated.delete(invoiceId);
        return updated;
      });
    });
  };

  /////////////////////// CANCELING ///////////////////////////////////////
  const [cancelingInvoiceIds, setCancelingInvoiceIds] = useState<Set<string>>(
    new Set()
  );

  const handleCancelInvoice = (merchantId: string, invoiceId: string) => {
    setCancelingInvoiceIds((prev) => new Set(prev).add(invoiceId));

    cancelInvoice(merchantId, invoiceId, {
      onSettled: () => {
        setCancelingInvoiceIds((prev) => {
          const updated = new Set(prev);
          updated.delete(invoiceId);
          return updated;
        });
      },
    });
  };

  const { cancelInvoice } = useCancelInvoice(
    () => {
      refetch();
      toaster.create({
        title: "Recaudo archivado",
        description: "El recaudo se ha archivado exitosamente.",
        type: "success",
        duration: 5000,
        meta: { closable: true },
      });
    },
    () => {
      toaster.create({
        title: "Error",
        description: "Hubo un error al archivar el recaudo.",
        type: "error",
        duration: 5000,
        meta: { closable: true },
      });
    }
  );

  const isCancellableInvoice = (invoice: Invoice) =>
    !invoice.paymentDate &&
    invoice.status !== "cancelled" &&
    (invoice.creationMethod !== "erp" || isAdmin);

  ////////////////////// EDITING //////////////////////////////////////////
  const [isEditingInvoice, setIsEditingInvoice] = useState(false);
  const [editInvoiceData, setEditInvoiceData] = useState<Invoice | undefined>(
    undefined
  );

  const editInvoiceManually = useEditCreateInvoice(
    () => {
      toaster.create({
        title: "Recaudo actualizado",
        description: "El recaudo se ha actualizado con éxito.",
        type: "success",
        duration: 5000,
        meta: { closable: true },
      });
      refetch();
    },
    () => {
      toaster.create({
        title: "Error",
        description: "Hubo un error al actualizar el recaudo.",
        type: "error",
        duration: 5000,
        meta: { closable: true },
      });
    }
  );

  async function handleEditInvoice(
    newInvoiceData: CreateInvoice
  ): Promise<void> {
    try {
      await editInvoiceManually(newInvoiceData);
    } finally {
      setIsEditingInvoice(false);
    }
  }

  ////////////////////// RETRY ERP ///////////////////////////////////////
  const [retryErpSyncInvoiceIds, setRetryErpSyncInvoiceIds] = useState<
    Set<string>
  >(new Set());

  const handleMarkManualErpSync = (merchantId: string, invoiceId: string) => {
    setRetryErpSyncInvoiceIds((prev) => new Set(prev).add(invoiceId));

    markManualErpSync(merchantId, invoiceId, {
      onSettled: () => {
        setRetryErpSyncInvoiceIds((prev) => {
          const updated = new Set(prev);
          updated.delete(invoiceId);
          return updated;
        });
      },
    });
  };

  const handleRetryErpSync = (merchantId: string, invoiceId: string) => {
    setRetryErpSyncInvoiceIds((prev) => new Set(prev).add(invoiceId));

    retryErpSync(merchantId, invoiceId, {
      onSettled: () => {
        setRetryErpSyncInvoiceIds((prev) => {
          const updated = new Set(prev);
          updated.delete(invoiceId);
          return updated;
        });
      },
    });
  };

  const { markManualErpSync } = useMarkManualErpSync(
    () => {
      toaster.create({
        title: "Recaudo actualizado",
        description: "Recuerda actualizar el pago en tu ERP.",
        type: "success",
        duration: 5000,
        meta: { closable: true },
      });
    },
    () => {
      toaster.create({
        title: "Error",
        description: "Hubo un problema marcando el recaudo como contabilizado.",
        type: "error",
        duration: 5000,
        meta: { closable: true },
      });
    }
  );

  const { retryErpSync } = useRetryErpSync(
    () => {
      toaster.create({
        title: "Reintentando contabilización",
        description: "El estado del recaudo se actualizará en unos momentos.",
        type: "success",
        duration: 5000,
        meta: { closable: true },
      });
    },
    () => {
      toaster.create({
        title: "Error",
        description: "Hubo un problema reintentando la contabilización.",
        type: "error",
        duration: 5000,
        meta: { closable: true },
      });
    }
  );

  ////////////////////// INVOICE MODALS //////////////////////////////
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | undefined>(
    undefined
  );

  const [isPaymentInfoModalOpen, setIsPaymentInfoModalOpen] = useState(false);
  const [isEditorsModalOpen, setIsEditorsModalOpen] = useState(false);
  const [isUnsuccessfulPaymentsModalOpen, setIsUnsuccessfulPaymentsModalOpen] =
    useState(false);

  return (
    <>
      <GenericTable isLoading={isLoading} columns={INVOICE_TABLE_COLUMNS}>
        {rows.map((invoice, index) => (
          <Table.Row key={index}>
            {INVOICE_TABLE_COLUMNS.map((column) => (
              <GenericTableElement
                key={`${index}-${column.key}`}
                value={column.value(invoice)}
                formatNumber={column.key === "amount"}
                badge={
                  column.key === "status" || column.key === "creationMethod"
                }
                booleanIcon={column.key === "sent"}
                statusIcon={column.key === "erpSyncStatus"}
                actions={
                  column.key === "actions" && !showDemo ? (
                    <Menu.Root>
                      <Menu.Trigger asChild>
                        <IconButton
                          aria-label="Menu"
                          variant="ghost"
                          size="xs"
                          _hover={{ bg: "gray.100" }}
                        >
                          <MoreVertical size="1.5rem" />
                        </IconButton>
                      </Menu.Trigger>
                      <Portal>
                        <Menu.Positioner>
                          <Menu.Content>
                            <Menu.Item
                              value="info"
                              onClick={() => {
                                setEditInvoiceData(invoice);
                                setIsEditingInvoice(true);
                              }}
                            >
                              <Pencil size="1.2rem" /> Información
                            </Menu.Item>
                            <Menu.Item
                              value="download"
                              onClick={() => {
                                if (merchant?.id) {
                                  handleDownloadInvoice(
                                    merchant.id,
                                    invoice.id
                                  );
                                }
                              }}
                              disabled={downloadingInvoiceIds.has(invoice.id)}
                            >
                              {downloadingInvoiceIds.has(invoice.id) ? (
                                <Spinner
                                  size="sm"
                                  color="merchants.primary.app"
                                />
                              ) : (
                                <>
                                  <Download size="1.2rem" /> Descargar
                                </>
                              )}
                            </Menu.Item>
                            {!!invoice.unsuccessfulPayments?.length && (
                              <Menu.Item
                                value="failed"
                                onClick={() => {
                                  setSelectedInvoice(invoice);
                                  setIsUnsuccessfulPaymentsModalOpen(true);
                                }}
                              >
                                <XCircle size="1.2rem" /> Pagos Fallidos
                              </Menu.Item>
                            )}
                            {(invoice.status === "paid" ||
                              invoice.status === "chargeback") && (
                              <Menu.Item
                                value="payment-info"
                                onClick={() => {
                                  setSelectedInvoice(invoice);
                                  setIsPaymentInfoModalOpen(true);
                                }}
                              >
                                <DollarSign size="1.2rem" /> Información de Pago
                              </Menu.Item>
                            )}
                            {isCancellableInvoice(invoice) && isEditor && (
                              <Menu.Item
                                value="archive"
                                onClick={() => {
                                  if (merchant?.id)
                                    handleCancelInvoice(
                                      merchant.id,
                                      invoice.id
                                    );
                                }}
                                disabled={cancelingInvoiceIds.has(invoice.id)}
                              >
                                {cancelingInvoiceIds.has(invoice.id) ? (
                                  <Spinner
                                    size="sm"
                                    color="merchants.primary.app"
                                  />
                                ) : (
                                  <>
                                    <Archive size="1.2rem" /> Archivar
                                  </>
                                )}
                              </Menu.Item>
                            )}
                            {invoice.erpSyncStatus === "error" && isEditor && (
                              <>
                                <Menu.Item
                                  value="mark-erp"
                                  onClick={() => {
                                    if (merchant?.id)
                                      handleMarkManualErpSync(
                                        merchant.id,
                                        invoice.id
                                      );
                                  }}
                                  disabled={retryErpSyncInvoiceIds.has(
                                    invoice.id
                                  )}
                                >
                                  <ClipboardList size="1.2rem" /> Marcar
                                  Contabilizado
                                </Menu.Item>
                                <Menu.Item
                                  value="retry-erp"
                                  onClick={() => {
                                    if (merchant?.id)
                                      handleRetryErpSync(
                                        merchant.id,
                                        invoice.id
                                      );
                                  }}
                                  disabled={retryErpSyncInvoiceIds.has(
                                    invoice.id
                                  )}
                                >
                                  <RefreshCw size="1.2rem" /> Reintentar
                                  Contabilización
                                </Menu.Item>
                              </>
                            )}
                            {isEditor &&
                              isInvoiceCardDisabled(invoice) &&
                              invoice.status === "ready" &&
                              merchant?.paymentMethods.card
                                .showWhenInactive && (
                                <Menu.Item
                                  value="toggle-card"
                                  onClick={() =>
                                    handleCardActivation(
                                      merchant.id,
                                      invoice.id,
                                      !invoice.cardPaymentActive
                                    )
                                  }
                                  disabled={cardActivationInvoiceIds.has(
                                    invoice.id
                                  )}
                                >
                                  {invoice.cardPaymentActive ? (
                                    <>
                                      <Ban size="1.2rem" /> Desactivar Tarjeta
                                    </>
                                  ) : (
                                    <>
                                      <CreditCard size="1.2rem" /> Activar
                                      Tarjeta
                                    </>
                                  )}
                                </Menu.Item>
                              )}
                            {isEditor &&
                              invoice.status === "ready" &&
                              !invoice.paymentDate &&
                              merchant?.allowsPartialPaymentPerInvoice &&
                              !merchant.allowsPartialPayment && (
                                <Menu.Item
                                  value="toggle-partial-payment"
                                  onClick={() =>
                                    setPartialPaymentActivation({
                                      rentalsMerchantId: merchant.id,
                                      id: invoice.id,
                                      active: !invoice.allowsPartialPayment,
                                    })
                                  }
                                  disabled={partialPaymentPending}
                                >
                                  {invoice.allowsPartialPayment ? (
                                    <>
                                      <PencilOff size="1.2rem" /> Desactivar
                                      Pago Parcial
                                    </>
                                  ) : (
                                    <>
                                      <Pencil size="1.2rem" /> Activar Pago
                                      Parcial
                                    </>
                                  )}
                                </Menu.Item>
                              )}
                            {isEditor &&
                              merchant?.erp === "paco" &&
                              invoice.status === "cancelled" &&
                              !invoice.paymentDate && (
                                <Menu.Item
                                  value="toggle-overdue-payment"
                                  onClick={() =>
                                    setCancelledPaymentActivation({
                                      rentalsMerchantId: merchant.id,
                                      id: invoice.id,
                                      active: !invoice.allowsCancelledPayment,
                                    })
                                  }
                                  disabled={overduePaymentPending}
                                >
                                  {invoice.allowsCancelledPayment ? (
                                    <>
                                      <BanknoteX size="1.2rem" />
                                      Deshabilitar Pago
                                    </>
                                  ) : (
                                    <>
                                      <Banknote size="1.2rem" />
                                      Habilitar Pago
                                    </>
                                  )}
                                </Menu.Item>
                              )}
                            {!!invoice.updatedBy?.length && (
                              <Menu.Item
                                value="editors"
                                onClick={() => {
                                  setSelectedInvoice(invoice);
                                  setIsEditorsModalOpen(true);
                                }}
                              >
                                <Hammer size="1.2rem" /> Editores
                              </Menu.Item>
                            )}
                          </Menu.Content>
                        </Menu.Positioner>
                      </Portal>
                    </Menu.Root>
                  ) : undefined
                }
              />
            ))}
          </Table.Row>
        ))}
      </GenericTable>

      <AddInvoiceModal
        open={isEditingInvoice}
        onClose={() => {
          setIsEditingInvoice(false);
          setEditInvoiceData(undefined);
        }}
        onSubmit={handleEditInvoice}
        editInvoiceData={editInvoiceData}
      />

      <PaymentInfoModal
        open={isPaymentInfoModalOpen}
        onClose={() => {
          setIsPaymentInfoModalOpen(false);
          setSelectedInvoice(undefined);
        }}
        paymentInfoInvoice={selectedInvoice}
      />

      <EditorsModal
        open={isEditorsModalOpen}
        onClose={() => {
          setIsEditorsModalOpen(false);
          setSelectedInvoice(undefined);
        }}
        editors={selectedInvoice?.updatedBy}
      />

      <UnsuccessfulPaymentsModal
        open={isUnsuccessfulPaymentsModalOpen}
        onClose={() => {
          setIsUnsuccessfulPaymentsModalOpen(false);
          setSelectedInvoice(undefined);
        }}
        unsuccessfulPayments={selectedInvoice?.unsuccessfulPayments}
      />
    </>
  );
};

export default InvoicesTable;
